import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Warehouse, Plus, Search, MapPin, Phone, Trash2, X, CheckCircle2 } from 'lucide-react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../../lib/api/client'
import { Button } from '../../components/ui/Button'
import { Input } from '../../components/ui/Input'
import { Badge } from '../../components/ui/Badge'
import { Skeleton } from '../../components/ui/Skeleton'
import toast from 'react-hot-toast'

export default function WarehousesPage() {
  const [search, setSearch] = useState('')
  const [isOpen, setIsOpen] = useState(false)
  const [form, setForm] = useState({
    name: '',
    code: '',
    address: '',
    phone: ''
  })

  const queryClient = useQueryClient()

  const { data, isLoading } = useQuery({
    queryKey: ['warehouses', { search }],
    queryFn: () => api.get('/inventory/warehouses', { params: { search } }).then(r => r.data),
  })

  const createMutation = useMutation({
    mutationFn: (newWh: typeof form) => api.post('/inventory/warehouses', newWh),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['warehouses'] })
      toast.success('انبار جدید اضافه شد')
      setIsOpen(false)
      setForm({ name: '', code: '', address: '', phone: '' })
    },
    onError: () => toast.error('خطا در ثبت انبار')
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/inventory/warehouses/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['warehouses'] })
      toast.success('انبار حذف شد')
    },
    onError: () => toast.error('خطا در حذف انبار')
  })

  const warehouses = data?.items || []

  return (
    <div className="p-4 space-y-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4"
      >
        <div>
          <h1 className="text-xl font-bold text-neutral-900 dark:text-white">مدیریت انبارها</h1>
          <p className="text-sm text-neutral-500 dark:text-neutral-400">تعریف و کنترل موقعیت‌های نگهداری کالا</p>
        </div>
        <Button onClick={() => setIsOpen(true)}>
          <Plus className="w-4 h-4 ml-1" />
          انبار جدید
        </Button>
      </motion.div>

      <div className="relative max-w-md">
        <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
        <Input
          placeholder="جستجوی انبار..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10"
        />
      </div>

      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3].map(i => (
            <div key={i} className="card p-5">
              <Skeleton className="h-6 w-32 mb-2" />
              <Skeleton className="h-4 w-48 mb-4" />
              <Skeleton className="h-2 w-full mb-2" />
              <Skeleton className="h-4 w-24" />
            </div>
          ))}
        </div>
      ) : warehouses.length === 0 ? (
        <div className="card p-12 text-center">
          <Warehouse className="w-12 h-12 mx-auto text-neutral-300 dark:text-neutral-600 mb-3" />
          <p className="text-neutral-500 dark:text-neutral-400">هنوز انباری تعریف نشده است</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {warehouses.map((wh: any) => (
            <motion.div
              key={wh.id}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="card p-5 hover:shadow-lg transition-shadow relative flex flex-col justify-between"
            >
              <div>
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-teal-100 dark:bg-teal-900/30 flex items-center justify-center">
                      <Warehouse className="w-5 h-5 text-teal-600 dark:text-teal-400" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-neutral-900 dark:text-white">{wh.name}</h3>
                      <p className="text-xs text-neutral-500 dark:text-neutral-400">{wh.code || 'WH-CODE'}</p>
                    </div>
                  </div>
                  <button
                    onClick={() => deleteMutation.mutate(wh.id)}
                    className="text-red-500 hover:text-red-700 p-1.5 rounded-lg hover:bg-red-50 dark:hover:bg-red-950/20"
                    title="حذف انبار"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
                {wh.address && (
                  <div className="flex items-center gap-2 text-sm text-neutral-600 dark:text-neutral-300 mb-1">
                    <MapPin className="w-3.5 h-3.5 text-neutral-400 shrink-0" />
                    <span>{wh.address}</span>
                  </div>
                )}
                {wh.phone && (
                  <div className="flex items-center gap-2 text-sm text-neutral-600 dark:text-neutral-300">
                    <Phone className="w-3.5 h-3.5 text-neutral-400 shrink-0" />
                    <span>{wh.phone}</span>
                  </div>
                )}
              </div>
              <div className="pt-3 mt-3 border-t border-neutral-100 dark:border-neutral-800 flex items-center justify-between">
                <Badge variant={wh.is_active !== false ? 'success' : 'neutral'}>
                  {wh.is_active !== false ? 'فعال' : 'غیرفعال'}
                </Badge>
                {wh.is_default && (
                  <span className="text-xs text-teal-600 font-medium flex items-center gap-1">
                    <CheckCircle2 className="w-3.5 h-3.5" /> انبار اصلی
                  </span>
                )}
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Modal create warehouse */}
      <AnimatePresence>
        {isOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white dark:bg-neutral-900 rounded-2xl p-6 w-full max-w-md shadow-xl border border-neutral-200 dark:border-neutral-800 space-y-4"
            >
              <div className="flex items-center justify-between pb-3 border-b border-neutral-100 dark:border-neutral-800">
                <h3 className="text-lg font-bold text-neutral-900 dark:text-white flex items-center gap-2">
                  <Warehouse className="w-5 h-5 text-teal-500" />
                  افزودن انبار جدید
                </h3>
                <button onClick={() => setIsOpen(false)} className="text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-200">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <form
                onSubmit={(e) => {
                  e.preventDefault()
                  if (!form.name) {
                    toast.error('نام انبار الزامی است')
                    return
                  }
                  createMutation.mutate(form)
                }}
                className="space-y-4"
              >
                <div>
                  <label className="block text-xs font-medium text-neutral-700 dark:text-neutral-300 mb-1">نام انبار</label>
                  <Input
                    placeholder="مثلا: انبار مرکزی"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-neutral-700 dark:text-neutral-300 mb-1">کد انبار</label>
                  <Input
                    placeholder="WH-01"
                    value={form.code}
                    onChange={(e) => setForm({ ...form, code: e.target.value })}
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-neutral-700 dark:text-neutral-300 mb-1">آدرس انبار</label>
                  <Input
                    placeholder="تهران، خیابان..."
                    value={form.address}
                    onChange={(e) => setForm({ ...form, address: e.target.value })}
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-neutral-700 dark:text-neutral-300 mb-1">شماره تماس</label>
                  <Input
                    placeholder="02188880000"
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  />
                </div>

                <div className="flex items-center justify-end gap-2 pt-2">
                  <Button type="button" variant="outline" onClick={() => setIsOpen(false)}>
                    انصراف
                  </Button>
                  <Button type="submit" isLoading={createMutation.isPending}>
                    ذخیره انبار
                  </Button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  )
}
